import React, { useEffect, useState } from "react";
import { InternalGrade } from "./InternalGrade/InternalGrade";
import { SoftSkills } from "./SoftSkills/SoftSkills";
import { Goals } from "./Goals/Goals";
import {
  CmSheetMenuEnum,
  CmSheetStateEnum,
  CM_SHEET_SLIDE_INFO_LINKS,
  PermissionsEnum,
  PERSONAL_PROFILE_SLIDE_INFO_LINKS,
} from "../../../../constants/AppConstants";
import { ProfessionalPathDevelopment } from "./ProfessionalDevelopment/ProfessionalPathDevelopment";
import { FormationCM } from "./FormationCM/FormationCM";
import { CollaboratorComment } from "./CollaboratorComment/CollaboratorComment";
import { AdminComment } from "./AdminComment/AdminComment";
import { FinalInterview } from "./FinalInterview/FinalInterview";
import { Note } from "./Note/Note";
import {
  CMSheetTitle,
  SlideInfoTooltipContent,
  StyledCMSheet,
  StyledCMSheetContent,
  StyledCMSheetFooter,
  StyledEmptyContainer,
  StyledLoaderContainer,
  StyledMenuContent,
  StyledTabContent,
} from "./SchedaCM.styled";
import { FontWeightEnum, IconSizeEnum, Role } from "../../../../types/AppTypes";
import {
  BodyTyp,
  TitlesTyp,
} from "../../../../components/Typography/Typography";
import { useAppSelector } from "../../../../hooks/store.hook";
import { roleHasPermission } from "../../../../utils/roleAuth.utils";
import {
  CmSheetStateMachine,
  CollaboratorCmSheet,
  CollaboratorDetails,
} from "../../../../types/api/collaborators.type";
import {
  StyledExpandMenuContent,
  StyledTabContainer,
} from "../CollaboratorProfileTabs.styled";
import {
  ExpandMenu,
  ExpandMenuOptionItem,
} from "../../../../components/ExpandMenu/ExpandMenu";
import { StatusHeader } from "./StatusHeader/StatusHeader";
import {
  CLICK_ACTION_LABEL,
  CM_SHEET_ARHIEVED_WITH_TERMINATION_MESSAGE,
  CM_SHEET_NOT_ACCESSIBLE_MESSAGE,
  COLLABORATOR_COMMENT_LABEL,
  ERROR_MESSAGE_LABEL,
  FINAL_INTERVIEW_LABEL,
  FORMATION_LABEL,
  GOALS_LABEL,
  ADMIN_COMMENTS_LABEL,
  INTERNAL_GRADE_LABEL,
  NOTE_LABEL,
  DEVELOPMENT_TITLE_LABEL,
  PROFESSIONAL_PATH_LABEL,
  SOFT_SKILLS_LABEL,
  USE_MANUAL_INFO_MESSAGE,
} from "../../../../constants/Dictionary";
import { Loader } from "../../../../components/core/Loader/Loader";
import { AuthProfile } from "../../../../types/api/auth.type";
import { currentStateIsLessThanTargetState } from "../../../../utils/cmSheet.utils";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import cmSheetArchivedWithTerminationImage from "../../../../images/cm_sheet_archived_with_termination.png";
import pendingCmSheetImage from "../../../../images/pending.png";
import errorImage from "../../../../images/error.png";
import { useEditing } from "../../../../providers/editing.provider";
import { ReactComponent as CMInfoIcon } from "../../../../icons/cmInfo.svg";
import {
  Tooltip,
  TooltipOpenModeEnum,
} from "../../../../components/core/Tooltip/Tooltip";
import { Link, LinkSizeEnum } from "../../../../components/core/Link/Link";
import { IconManager } from "../../../../components/core/IconManager/IconManager";
import { theme } from "../../../../constants/StyledComponents";
import { DevelopmentCM } from "./DevelopmentCM/DevelopmentCM";
import {
  useCmSheetStateMachine,
  useUpdateCmSheetStateMachine,
} from "../../../../hooks/cmSheet/stateMachine.hook";
import { isProd, isQA } from "../../../../utils/generals.utils";

interface SchedaCMProps {
  cmSheetInfo: CollaboratorCmSheet;
  collaborator: CollaboratorDetails;
  isInterim: boolean;
  isMyProfile?: boolean;
}

export interface CmSheetSectionProps {
  footer: (component: React.ReactNode) => void;
  cmSheetInfo: CollaboratorCmSheet;
  collaborator: CollaboratorDetails;
  cmSheetStateMachineInfo: CmSheetStateMachine;
  refetchCmSheetStateMachine: () => Promise<unknown>;
  setHeaderRightContent: (component: React.ReactNode) => void;
  showFooter: boolean;
  setHeaderTextSwitch: (component: React.ReactNode) => void;
  isInterim: boolean;
  isMyProfile?: boolean;
  edit: boolean;
  setEdit: (value: boolean) => void;
}

const SchedaCM = ({
  cmSheetInfo,
  collaborator,
  isMyProfile,
  isInterim,
}: SchedaCMProps) => {
  const { profile } = useAppSelector((state) => state.user);
  const [headerTextSwitch, setHeaderTextSwitch] =
    useState<React.ReactNode | null>(null);
  const [headerRightContent, setHeaderRightContent] =
    useState<React.ReactNode | null>(null);

  const { edit, setEdit, confirmLeave } = useEditing();

  const isHRorAdmin = () => {
    return profile.role === Role.ADMIN || profile.role === Role.HR;
  };

  const [footer, setFooter] = useState<React.ReactNode | null>(null);
  const { isUpdating: isStateMachineUpdating } = useUpdateCmSheetStateMachine(
    cmSheetInfo.id,
  );

  const {
    data: cmSheetStateMachineInfo,
    isLoading: loadingCmStateMachine,
    isError,
    refetch,
  } = useCmSheetStateMachine(cmSheetInfo.id);

  const isFinalFeedbackSectionDisabled = () => {
    if (!cmSheetStateMachineInfo) {
      return false;
    }

    if (
      isMyProfile &&
      currentStateIsLessThanTargetState(
        cmSheetStateMachineInfo.currentState,
        CmSheetStateEnum.FINAL_FEEDBACK,
      )
    ) {
      return true;
    }

    if (isMyProfile && !cmSheetStateMachineInfo.isRevisionRequestSent) {
      return true;
    }

    if (
      !isHRorAdmin() &&
      cmSheetStateMachineInfo.currentState === CmSheetStateEnum.DRAFT
    ) {
      return true;
    }

    return false;
  };

  const getCmSheetMenu = (
    profile: AuthProfile,
  ): ExpandMenuOptionItem<CmSheetMenuEnum>[] => {
    let menuItems: ExpandMenuOptionItem<CmSheetMenuEnum>[] = [
      {
        label: INTERNAL_GRADE_LABEL,
        value: CmSheetMenuEnum.INTERNAL_GRADE,
      },
      {
        label: SOFT_SKILLS_LABEL,
        value: CmSheetMenuEnum.SOFT_SKILLS,
      },
      {
        label: PROFESSIONAL_PATH_LABEL,
        value: CmSheetMenuEnum.PROFESSIONAL_PATH,
      },
      {
        label: GOALS_LABEL,
        value: CmSheetMenuEnum.GOALS,
      },
      {
        label: DEVELOPMENT_TITLE_LABEL,
        value: CmSheetMenuEnum.DEVELOPMENT,
      },
      {
        label: FORMATION_LABEL,
        value: CmSheetMenuEnum.FORMATION,
        disabled: isProd() || isQA(),
      },
      {
        label: COLLABORATOR_COMMENT_LABEL,
        value: CmSheetMenuEnum.COLLABORATOR_COMMENT,
      },
      {
        label: FINAL_INTERVIEW_LABEL,
        value: CmSheetMenuEnum.FINAL_INTERVIEW,
        disabled: isFinalFeedbackSectionDisabled(),
      },
      ...(!isMyProfile &&
      roleHasPermission(profile, PermissionsEnum.EDIT_ADMIN_COMMENT)
        ? [
            {
              label: ADMIN_COMMENTS_LABEL,
              value: CmSheetMenuEnum.ADMIN_COMMENT,
            },
          ]
        : []),
      ...(!isMyProfile &&
      (roleHasPermission(profile, CmSheetMenuEnum.NOTE) ||
        (profile.isCm && profile.id === cmSheetInfo.careerManagerId))
        ? [
            {
              label: NOTE_LABEL,
              value: CmSheetMenuEnum.NOTE,
            },
          ]
        : []),
    ];

    return menuItems;
  };

  const [cmSheetSelectedMenu, setCmSheetSelectedMenu] = useState<
    ExpandMenuOptionItem<CmSheetMenuEnum>
  >(getCmSheetMenu(profile)[0]);

  const handleCmSheetMenuSwitch = (
    menu: ExpandMenuOptionItem<CmSheetMenuEnum>,
  ) => {
    if (menu.value !== cmSheetSelectedMenu.value) {
      setFooter(null);
      setHeaderRightContent(null);
    }
    setCmSheetSelectedMenu(menu);
    setHeaderRightContent(null);
  };

  useEffect(() => {
    if (
      cmSheetSelectedMenu.value !== CmSheetMenuEnum.FORMATION &&
      cmSheetSelectedMenu.value !== CmSheetMenuEnum.DEVELOPMENT
    ) {
      setHeaderTextSwitch(null);
    }
    if (
      cmSheetSelectedMenu.value !== CmSheetMenuEnum.GOALS &&
      cmSheetSelectedMenu.value !== CmSheetMenuEnum.DEVELOPMENT
    ) {
      setHeaderRightContent(null);
    }
  }, [cmSheetSelectedMenu]);

  const showLoader =
    isStateMachineUpdating ||
    (loadingCmStateMachine && cmSheetStateMachineInfo == null);
  const showError =
    !loadingCmStateMachine && isError && cmSheetStateMachineInfo == null;

  const showFooter = () => {
    //La scheda non è dell'anno corrente
    if (!cmSheetInfo.isCurrent) {
      return false;
    }

    //La scheda è archiviata o archiviata con cessazione
    if (
      cmSheetStateMachineInfo &&
      [
        CmSheetStateEnum.ARCHIVED_SHEET,
        CmSheetStateEnum.ARCHIVED_SHEET_WITH_TERMINATION,
      ].includes(cmSheetStateMachineInfo.currentState)
    ) {
      return false;
    }

    return true;
  };

  const renderCMSheet = () => {
    if (!cmSheetStateMachineInfo) {
      return null;
    }

    const cmSheetSectionProps: CmSheetSectionProps = {
      cmSheetInfo,
      collaborator,
      showFooter: showFooter(),
      cmSheetStateMachineInfo,
      footer: setFooter,
      refetchCmSheetStateMachine: refetch,
      setHeaderRightContent,
      setHeaderTextSwitch,
      isMyProfile,
      isInterim,
      edit,
      setEdit,
    };

    const getSectionInfoIcon = (section: CmSheetMenuEnum) => {
      let infoLink: string | undefined = undefined;
      if (isMyProfile) {
        infoLink = PERSONAL_PROFILE_SLIDE_INFO_LINKS[section];
      } else {
        infoLink = CM_SHEET_SLIDE_INFO_LINKS[section];
      }

      if (!infoLink) {
        return null;
      }

      return (
        <Tooltip
          openMode={TooltipOpenModeEnum.CLICK}
          message={
            <SlideInfoTooltipContent>
              <Link
                onClick={() => window.open(infoLink, "_blank")}
                size={LinkSizeEnum.SMALL}
                color={theme.colors.purple_hover}
              >
                {CLICK_ACTION_LABEL}
              </Link>
              <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
                {USE_MANUAL_INFO_MESSAGE}
              </BodyTyp>
            </SlideInfoTooltipContent>
          }
        >
          <IconManager noStrokeColor size={IconSizeEnum.SMALL}>
            <CMInfoIcon />
          </IconManager>
        </Tooltip>
      );
    };

    const getSectionTitle = (section: CmSheetMenuEnum): string => {
      const menuItems = getCmSheetMenu(profile);
      return menuItems.find((item) => item.value === section)?.label ?? "";
    };

    const cmSheetIsNotReadableInMyProfile = () => {
      if (!cmSheetInfo.isCurrent) {
        return false;
      }

      return (
        isMyProfile && !cmSheetStateMachineInfo.isCMSheetVisibleToCollaborator
      );
    };

    const cmSheetIsArchivedWithTerminationMode = () => {
      if (
        isMyProfile &&
        cmSheetStateMachineInfo.currentState ===
          CmSheetStateEnum.ARCHIVED_SHEET_WITH_TERMINATION
      ) {
        return true;
      }

      return (
        cmSheetStateMachineInfo.currentState ===
          CmSheetStateEnum.ARCHIVED_SHEET_WITH_TERMINATION &&
        !roleHasPermission(
          profile,
          PermissionsEnum.READ_CM_SHEET_WHEN_ARCHIVED_WITH_TERMINATION,
        )
      );
    };

    return cmSheetIsArchivedWithTerminationMode() ? (
      <StyledEmptyContainer>
        {loadingCmStateMachine ? null : (
          <EmptyState
            label={CM_SHEET_ARHIEVED_WITH_TERMINATION_MESSAGE}
            imageSrc={cmSheetArchivedWithTerminationImage}
          />
        )}
      </StyledEmptyContainer>
    ) : cmSheetIsNotReadableInMyProfile() ? (
      <StyledEmptyContainer>
        {loadingCmStateMachine ? null : (
          <EmptyState
            label={CM_SHEET_NOT_ACCESSIBLE_MESSAGE}
            imageSrc={pendingCmSheetImage}
          />
        )}
      </StyledEmptyContainer>
    ) : (
      <StyledTabContainer>
        <StyledTabContent>
          <StatusHeader
            cmSheetInfo={cmSheetInfo}
            cmSheetStateMachineInfo={cmSheetStateMachineInfo}
            isMyProfile={isMyProfile}
            isInterim={isInterim}
          />
          <StyledMenuContent>
            <ExpandMenu
              data={getCmSheetMenu(profile)}
              menuSelected={cmSheetSelectedMenu}
              setMenuSelected={handleCmSheetMenuSwitch}
              confirmLeave={confirmLeave}
            />
            <StyledExpandMenuContent>
              <StyledCMSheet>
                <CMSheetTitle>
                  <div className="title-switch">
                    <div className="title-icon">
                      <TitlesTyp
                        variant="2"
                        fontWeight={FontWeightEnum.SEMIBOLD}
                      >
                        {getSectionTitle(cmSheetSelectedMenu.value)}
                      </TitlesTyp>
                      {getSectionInfoIcon(cmSheetSelectedMenu.value)}
                    </div>
                    {headerTextSwitch}
                  </div>
                  {headerRightContent}
                </CMSheetTitle>
                <StyledCMSheetContent>
                  {cmSheetSelectedMenu.value ===
                    CmSheetMenuEnum.INTERNAL_GRADE && (
                    <InternalGrade {...cmSheetSectionProps} />
                  )}
                  {cmSheetSelectedMenu.value ===
                    CmSheetMenuEnum.SOFT_SKILLS && (
                    <SoftSkills {...cmSheetSectionProps} />
                  )}
                  {cmSheetSelectedMenu.value === CmSheetMenuEnum.GOALS && (
                    <Goals {...cmSheetSectionProps} />
                  )}
                  {cmSheetSelectedMenu.value ===
                    CmSheetMenuEnum.PROFESSIONAL_PATH && (
                    <ProfessionalPathDevelopment {...cmSheetSectionProps} />
                  )}
                  {cmSheetSelectedMenu.value ===
                    CmSheetMenuEnum.DEVELOPMENT && (
                    <DevelopmentCM {...cmSheetSectionProps} />
                  )}
                  {cmSheetSelectedMenu.value === CmSheetMenuEnum.FORMATION && (
                    <FormationCM {...cmSheetSectionProps} />
                  )}
                  {cmSheetSelectedMenu.value ===
                    CmSheetMenuEnum.COLLABORATOR_COMMENT && (
                    <CollaboratorComment {...cmSheetSectionProps} />
                  )}
                  {cmSheetSelectedMenu.value ===
                    CmSheetMenuEnum.FINAL_INTERVIEW && (
                    <FinalInterview {...cmSheetSectionProps} />
                  )}
                  {cmSheetSelectedMenu.value ===
                    CmSheetMenuEnum.ADMIN_COMMENT && (
                    <AdminComment {...cmSheetSectionProps} />
                  )}
                  {cmSheetSelectedMenu.value === CmSheetMenuEnum.NOTE && (
                    <Note {...cmSheetSectionProps} />
                  )}
                </StyledCMSheetContent>
              </StyledCMSheet>
              <StyledCMSheetFooter>{footer}</StyledCMSheetFooter>
            </StyledExpandMenuContent>
          </StyledMenuContent>
        </StyledTabContent>
      </StyledTabContainer>
    );
  };

  return (
    <>
      {showLoader ? (
        <StyledTabContainer>
          <StyledLoaderContainer>
            <Loader loading={true} />
          </StyledLoaderContainer>
        </StyledTabContainer>
      ) : null}
      {showError ? (
        <StyledTabContainer>
          <div className="message-container">
            <EmptyState imageSrc={errorImage} label={ERROR_MESSAGE_LABEL} />
          </div>
        </StyledTabContainer>
      ) : null}
      {!showLoader && !showError ? renderCMSheet() : null}
    </>
  );
};

export default SchedaCM;
